import React, { useContext, useEffect, useState } from 'react';
import AuthHeader from '../../commons/AuthHeader';
import AuthFooter from '../../commons/AuthFooter';
import { Button, ButtonVariant, IconName, ButtonIconAlign, Icon, IconSize } from '@eightfold.ai/octuple';
import { globalContext } from '../../store';
import { getBrandValues, getBrandImages, dispatchToGlobalState, getAssessmentDuration, updateRaterStatus } from '../../commons/commonFunctions';
import { useLocation, useNavigate } from 'react-router-dom';
import { actionButtonStyle, navReturnBtnStyle } from '../../commons/commonStyles';
import { clientInfo } from '../../commons/ClientDynamicInfo';
import InfoBanner from '../../commons/InfoBanner';
const WelcomeRaterSurvey = () => {
  const navigate = useNavigate();
  const { globalState, dispatch } = useContext(globalContext);

  const title = globalState?.clientData?.name;
  const location = useLocation();
  const receivedData = location.state;
  const [showLoader, setShowLoader] = useState(false);
  const [headerLogo, setHeaderLogo] = useState<string>('');
  const [, setHeaderImage] = useState<string>('');
  const [, setPrimaryColor] = useState<string>('#BA3A19');

  // Extract assessment data from navigation state
  const assessmentData = receivedData?.assessmentData;
  const assessmentName = assessmentData?.title || '360 Survey';
  // Get duration from sections durationText or fall back to category-based duration
  const assessmentDuration = assessmentData?.sections?.[0]?.durationText || 
                             getAssessmentDuration(assessmentData?.category) || 
                             '~30 min';
  const raterDetails = receivedData?.raterDetails;
  
  // Match focusEmail with raterEmail in ratersData
  const focusEmail = raterDetails?.assignmentData?.focusEmail;
  const matchedRater = raterDetails?.ratersData?.find(
    (rater: any) => rater.raterEmail === focusEmail
  );

  // True when every ratersConfig category has reached its maximum invited count
  const isAllMaxFilled = (raterDetails?.ratersConfig?.length > 0) &&
    raterDetails?.ratersConfig?.every((config: any) => {
      const count = raterDetails?.ratersData?.filter(
        (rater: any) => rater.type === config.name
      ).length ?? 0;
      return count >= config.maximum;
    });
  
  // Set header images and colors based on client branding or fallback to defaults
  const applyClientBranding = () => {
    setHeaderImage(clientInfo[title].headerImage);
    setHeaderLogo(clientInfo[title].headerLogo);
  };

  const applyGlobalBranding = () => {
    const headerLogoId = getBrandValues(globalState?.clientData?.branding, "COMPANY_LOGO");
    const headerImgId = getBrandValues(globalState?.clientData?.branding, "WELCOME_PAGE_HEADER_IMAGE");
    if (globalState?.headerLogo) {
      setHeaderLogo(globalState?.headerLogo);
    } else {
      getBrandImages(headerLogoId, setHeaderLogo, "headerLogo", dispatch, "SET_HEADER_LOGO");
    }

    if (globalState?.headerImg) {
      setHeaderImage(globalState?.headerImg);
    } else {
      getBrandImages(headerImgId, setHeaderImage, "headerImage", dispatch, "SET_HEADER_IMG");
    }

    if (globalState?.primaryColor) {
      setPrimaryColor(globalState?.primaryColor);
    } else {
      const primColor = getBrandValues(globalState?.clientData?.branding, 'PRIMARY_COLOR')?.value || '#BA3A19';
      dispatchToGlobalState(setPrimaryColor, primColor, dispatch, 'SET_PRIMARY_COLOR');
    }
  };

  useEffect(() => {
    document.documentElement.scrollTo(0, 0);
    if (clientInfo.hasOwnProperty(title)) {
      applyClientBranding();
    } else {
      applyGlobalBranding();
    }

    if (!raterDetails || Object.keys(raterDetails).length === 0) {
      navigate('/dashboard');
    }
  }, []);

  const handleInviteRaters = () => {
    navigate('/invite-raters', {
      state: { raterDetails: raterDetails, isReviewMode: isAllMaxFilled },
    });
  }

  const launchAssessment = () => {
    const isHeidrick = raterDetails?.assignmentData?.sourceName === 'Heidrick';
    const assessmentUrl =  isHeidrick ? `/assessment${raterDetails?.assessmentUrl}` : raterDetails?.assessmentUrl;
    let isHeidrickData = {};
    if( isHeidrick ){
      isHeidrickData = {
        assessmentId: raterDetails?.assignmentData?.assessmentId,
        assessmentStructureId: raterDetails?.assignmentData?.assessmentStructureId,
        surveyTemplate: raterDetails?.assignmentData?.surveyTemplate,
        surveyNameLeaders: raterDetails?.assignmentData?.surveyNameLeaders,
        surveyNameRaters: raterDetails?.assignmentData?.surveyNameRaters,
        isSelfAssessment: true,
      };
    }

    const currentStatus = matchedRater?.status;
    if (currentStatus === 'In Progress' || currentStatus === 'P') {
      // Status already In Progress — skip redundant API call and navigate directly
      if (isHeidrick) {
        navigate(assessmentUrl, { state: { raterDetails: { ...isHeidrickData } } });
      } else {
        window.open(assessmentUrl, '_self');
      }
      return;
    }

    updateRaterStatus({
      assignmentId: raterDetails?.assignmentData?.assignmentId,
      assessmentStructureId: raterDetails?.assignmentData?.assessmentStructureId,
      status: "In Progress",
      raterId: matchedRater?._id,
      showNotifications: false,
      url: assessmentUrl,
      setLoader: setShowLoader,
      isInhouse: isHeidrick,
      isHeidrickData: isHeidrickData,
      navigate: navigate
    });
  };

  return (
    <div className='mainWrapper'>
      <div className='innerWrapper'>
        <AuthHeader title={title} headerLogo={headerLogo} setLoader={setShowLoader} />
        <div className='middleWrapper'>
          <div className='leftTextWrapper'>
            <div>
              <Button
                text='Back to dashboard'
                variant={ButtonVariant.Secondary}
                classNames='completedAssessmentButton'
                onClick={() => navigate('/dashboard')}
                transparent={true}
                iconProps={{ path: IconName.mdiChevronLeft }}
                alignIcon={ButtonIconAlign.Left}
              />
            </div>
            <div className='textContainer'>
              <h1 className='textHeading'>{receivedData?.ratersInvited ? `Ready to take the ${assessmentName}?` : `Welcome to the ${assessmentName}`}</h1>
              <h2 className='textDescription'>
                In the 360 self-questionnaire, you rate yourself on your own leadership behaviors across key dimensions (such as, collaboration, execution, and adaptability), creating a baseline view that is compared with feedback from others.
              </h2>
              <div className='assessmentActionItems'>
                {(matchedRater?.status === 'Completed' || matchedRater?.status === 'C') && (
                  <div className='assessmentCompletedText' style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon path={IconName.mdiCheck} size={IconSize.Medium} color='#002136' />
                    <span style={{ fontWeight: 500, color: '#002136' }}>Assessment completed</span>
                  </div>
                )}
          
                {(raterDetails?.ratersData?.some((rater: any) => rater.type !== 'Self' && (rater.raterProfileId || receivedData?.ratersInvited)) && (matchedRater?.status !== 'Completed')) && (
                  <Button
                    text='Launch assessment'
                    variant={ButtonVariant.Secondary}
                    classNames='actionButton'
                    style={actionButtonStyle}
                    onClick={launchAssessment}
                    alignIcon={ButtonIconAlign.Right}
                    disabled={showLoader}
                    iconProps={showLoader ? { path: IconName.mdiLoading, spin: true } : {path:IconName.mdiArrowRight}}
                  />
                )}

                <Button
                  text={isAllMaxFilled ? 'Review raters' : (receivedData?.ratersInvited ? 'Nominate more raters' : 'Nominate Raters')}
                  variant={ButtonVariant.Secondary}
                  classNames={(raterDetails?.ratersData?.length > 1) ? 'navReturnButton actionButton' : 'actionButton'}
                  style={(raterDetails?.ratersData?.length > 1) ? navReturnBtnStyle : actionButtonStyle}
                  onClick={handleInviteRaters}
                  iconProps={(raterDetails?.ratersData?.length > 1) ? undefined : { path: IconName.mdiArrowRight }}
                  alignIcon={ButtonIconAlign.Right}
                />
              </div>
            </div>
          </div>

          <div className='rightTextWrapper'>
            <div className='bgImageWrapper' style={{backgroundImage: `url(./../assets/img/welcomeRaterSurveyBg.png)`, backgroundSize: '60%'}}>
              <div className='contentOverlay'>
                <div className='Textcontent'>
                  <div className='textSection'>
                    <div className='sectionDescription'>
                      <div className='sectionCard' style={{width: '100%'}}>
                        {receivedData?.ratersInvited ? <img src={'../../assets/img/Success.svg'} alt='invite raters' className='cardIcon'/>: <img src={'../../assets/img/invite-raters.svg'} alt='invite raters' className='cardIcon'/>}
                        <div className='agilityCardTitle'>Nominate Raters</div>
                        <div className='agilityCardDescription'>
                          Before completing your own 360 survey, you must first nominate raters.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='textSection'>
                    <h3 className='sectionHeading' style={{marginBottom:'0px'}}>What to know before you start</h3>
                    <div className='sectionDescription'>
                      <div className='listSection' style={{width:'100%'}}>
                        {/* <div className='listTitle'>Time & Environment</div> */}
                        <div className='listItem'>
                          <img src={'../../assets/img/dot.svg'} alt='dot' />
                          <div className='listText'>Nominate raters from multiple categories.</div>
                        </div>
                        <hr className='listItemSeparator' style={{border:'1px solid #E0E1E5'}}/>

                        <div className='listItem'>
                          <img src={'../../assets/img/dot.svg'} alt='count'/>
                          <div className='listText'>We suggest nominating at least the minimum number of raters recommended for each, although more references are always encouraged.</div>
                        </div>
                        <hr className='listItemSeparator' style={{border:'1px solid #E0E1E5'}}/>
                        
                        <div className='listItem'>
                          <img src={'../../assets/img/dot.svg'} alt='mute'/>
                          <div className='listText'>If you don’t have any raters in a category, leave it blank.</div>
                        </div>
                        {receivedData?.ratersInvited && !receivedData?.isRequiredRatersFilled ? (
                          <InfoBanner 
                            message="You haven't nominated the recommended minimum number of raters"
                            style={{ margin: '24px 0' }}
                          />
                          )
                          :
                          <>
                            <hr className='listItemSeparator' style={{border:'1px solid #E0E1E5'}}/>

                            <div className='listItem'>
                              <img src={'../../assets/img/dot.svg'} alt='mute'/>
                              <div className='listText'>Duration: <span>30-40 minutes</span></div>
                            </div>
                          </>
                        }
                      </div>

                      
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <AuthFooter />
      </div>
    </div>
  );
};

export default WelcomeRaterSurvey;
